/**
 * @fileoverview About Page
 * The "station corridor" - explaining who we are and what we do.
 * 
 * @module pages/about
 */

import { el } from "../shared/components/simpleRender.js";

/**
 * Features displayed in the "What You'll Find" section.
 * @type {Array<{icon: string, title: string, description: string}>}
 */
const FEATURES = [
  {
    icon: "🔭",
    title: "Observatory Network",
    description: "Specialized dashboards monitoring the fault lines of global finance — from dollar liquidity to funding stress, credit deterioration to contagion risk."
  },
  {
    icon: "📡",
    title: "Signal Engine",
    description: "Real-time indicators sourced from authoritative public data and transformed through deterministic pipelines. Cross-currency basis, TGA dynamics, repo stress, curve structure — the signals beneath the headlines."
  },
  {
    icon: "📜",
    title: "Dispatches",
    description: "Long-form analysis from the macro watchtower. Regimes, mechanics, and uncomfortable truths that don't fit neatly into mainstream narratives."
  },
  {
    icon: "🏛️",
    title: "Capitulation Station",
    description: "Coming soon. A forum for the doom-pilled macro community — where charts matter more than takes, and signal beats sentiment."
  }
];

/**
 * AI contracting principles for the "100% AI-Generated" section.
 * @type {Array<{strong: string, text: string}>}
 */
const AI_PRINCIPLES = [
  {
    strong: "Explicit contracts first",
    text: "inputs, outputs, behaviors, and error semantics are defined before implementation"
  },
  {
    strong: "Deterministic execution",
    text: "the same inputs always produce the same outputs"
  },
  {
    strong: "Composable modules",
    text: "small, auditable units with no hidden coupling"
  },
  {
    strong: "No black boxes",
    text: "every transformation is traceable end-to-end"
  },
  {
    strong: "Human-defined architecture, AI-generated implementation",
    text: ""
  }
];

/**
 * Philosophy principles for the "How We Build" section.
 * @type {Array<{strong: string, text: string}>}
 */
const PHILOSOPHY_PRINCIPLES = [
  { strong: "Deterministic", text: "over clever" },
  { strong: "Auditable", text: "over opaque" },
  { strong: "Composable", text: "over monolithic" },
  { strong: "Signal-driven", text: "over narrative-driven" }
];

/**
 * Build principles for the "How We Build" section.
 * @type {Array<{strong: string, text: string}>}
 */
const BUILD_PRINCIPLES = [
  {
    strong: "Small, focused modules",
    text: "each component does exactly one job"
  },
  {
    strong: "Minimal dependencies",
    text: "we avoid fragile frameworks and hidden magic"
  },
  {
    strong: "Explicit data contracts",
    text: "every flow is documented and verifiable"
  },
  {
    strong: "AI as an implementation engine",
    text: "humans define intent, AI executes precisely"
  }
];

/**
 * Audience segments for the "Who This Is For" section.
 * @type {Array<{title: string, description: string}>}
 */
const AUDIENCES = [
  {
    title: "Macro Practitioners",
    description: "Investors, traders, and analysts who need real visibility into liquidity and stress, not recycled talking points."
  },
  {
    title: "Market Students",
    description: "Those learning how crises actually form — how funding breaks, how contagion spreads, how capitulation unfolds."
  },
  {
    title: "Curious Observers",
    description: "Anyone who wants to understand the forces moving markets without the spin."
  }
];

/**
 * Platform result attributes for the AI section.
 * @type {Array<string>}
 */
const PLATFORM_RESULTS = [
  "Predictable",
  "Auditable",
  "Reproducible",
  "Safe to evolve",
  "And shockingly fast to build"
];

/**
 * Creates a feature card element.
 */
function createFeatureCard({ icon, title, description }) {
  return el("div", { className: "about-feature-card" }, [
    el("div", { className: "about-feature-icon" }, [icon]),
    el("h3", { className: "about-feature-title" }, [title]),
    el("p", { className: "about-feature-desc" }, [description])
  ]);
}

/**
 * Creates a principle list item.
 */
function createPrincipleItem({ strong, text }) {
  if (text === "") {
    return el("li", {}, [el("strong", {}, [strong])]);
  }
  return el("li", {}, [
    el("strong", {}, [strong]),
    " — ",
    text
  ]);
}

/**
 * Creates a philosophy principle item (compact style).
 */
function createPhilosophyItem({ strong, text }) {
  return el("li", {}, [
    el("strong", {}, [strong]),
    " ",
    text
  ]);
}

/**
 * Creates an audience card element.
 */
function createAudienceCard({ title, description }) {
  return el("div", { className: "about-audience-card" }, [
    el("h3", {}, [title]),
    el("p", {}, [description])
  ]);
}

/**
 * Renders the About page.
 * @returns {HTMLElement}
 */
export function renderAboutPage() {
  return el("div", { className: "about-page" }, [
    // Background layer
    el("div", { className: "about-bg-layer", "aria-hidden": "true" }, []),
    el("div", { className: "about-overlay", "aria-hidden": "true" }, []),

    // Page content
    el("div", { className: "about-page-content" }, [
      // Hero Section
      el("header", { className: "about-hero" }, [
        el("h1", { className: "about-hero-title" }, ["About Capitulators.io"]),
        el("p", { className: "about-hero-tagline" }, [
          "A gothic macro terminal for those who watch the markets with clear eyes and steady nerves."
        ])
      ]),

      // Main Content
      el("main", { className: "about-main" }, [
        // Who We Are
        el("section", { className: "about-section reveal-section" }, [
          el("div", { className: "about-section-header" }, [
            el("span", { className: "about-section-icon" }, ["⊙"]),
            el("h2", { className: "about-section-title" }, ["Who We Are"])
          ]),
          el("div", { className: "about-section-body" }, [
            el("p", { className: "about-lead-paragraph" }, [
              "Capitulators.io exists because no one else was watching the gauges."
            ]),
            el("p", {}, [
              "While financial media chases headlines and price action, we track the signals that actually matter: global liquidity flows, funding stress, dollar dynamics, and the plumbing that holds the system together."
            ]),
            el("p", {}, [
              "We are macro practitioners, systems architects, and data engineers who got tired of noise — tired of narratives that explain yesterday's move but predict nothing, tired of tools built for engagement rather than insight."
            ]),
            el("p", {}, [
              "So we built something different."
            ]),
            el("p", {}, [
              "A terminal for the liquidity-pilled. A watchtower for those who understand that stress precedes crisis, and that preparation matters more than prediction."
            ])
          ])
        ]),

        // 100% AI-Generated Platform
        el("section", { className: "about-section reveal-section about-ai-section" }, [
          el("div", { className: "about-section-header" }, [
            el("span", { className: "about-section-icon" }, ["⟁"]),
            el("h2", { className: "about-section-title" }, ["A 100% AI-Generated Platform — By Design"])
          ]),
          el("div", { className: "about-section-body" }, [
            el("p", { className: "about-lead-paragraph" }, [
              "Capitulators.io is entirely AI-generated."
            ]),
            el("p", {}, [
              "Not AI-assisted. Not \"AI helped with a few scripts.\" Every line of code, every signal definition, every transformation, every visualization is produced by artificial intelligence."
            ]),
            el("p", {}, [
              "What makes this possible — and safe at enterprise scale — is not the AI itself, but how we constrain it."
            ]),
            el("p", {}, [
              "We operate a ",
              el("strong", {}, ["deterministic contracting system"]),
              " that governs how AI builds software:"
            ]),
            el("ul", { className: "about-principles-list" },
              AI_PRINCIPLES.map(createPrincipleItem)
            ),
            el("p", {}, [
              "This allows us to use AI as a force multiplier, not a liability."
            ]),
            el("div", { className: "about-result-box" }, [
              el("p", { className: "about-result-label" }, ["The result is a platform that is:"]),
              el("ul", { className: "about-result-list" },
                PLATFORM_RESULTS.map(item => el("li", {}, [item]))
              )
            ]),
            el("p", { className: "about-build-note" }, [
              "We don't prompt and hope. We contract, constrain, and verify."
            ])
          ])
        ]),

        // What You'll Find
        el("section", { className: "about-section reveal-section" }, [
          el("div", { className: "about-section-header" }, [
            el("span", { className: "about-section-icon" }, ["◎"]),
            el("h2", { className: "about-section-title" }, ["What You'll Find Here"])
          ]),
          el("div", { className: "about-section-body" }, [
            el("div", { className: "about-feature-grid" }, 
              FEATURES.map(createFeatureCard)
            )
          ])
        ]),

        // How We Build
        el("section", { className: "about-section reveal-section" }, [
          el("div", { className: "about-section-header" }, [
            el("span", { className: "about-section-icon" }, ["⚙"]),
            el("h2", { className: "about-section-title" }, ["How We Build (Without the Hype)"])
          ]),
          el("div", { className: "about-section-body" }, [
            el("p", {}, [
              "We believe modern systems should be:"
            ]),
            el("ul", { className: "about-philosophy-list" },
              PHILOSOPHY_PRINCIPLES.map(createPhilosophyItem)
            ),
            el("p", {}, [
              "Our architecture follows a strict philosophy:"
            ]),
            el("ul", { className: "about-principles-list" },
              BUILD_PRINCIPLES.map(createPrincipleItem)
            ),
            el("p", { className: "about-build-note" }, [
              "This isn't experimental. It's already running — at enterprise scale."
            ])
          ])
        ]),

        // Who This Is For
        el("section", { className: "about-section reveal-section" }, [
          el("div", { className: "about-section-header" }, [
            el("span", { className: "about-section-icon" }, ["👁"]),
            el("h2", { className: "about-section-title" }, ["Who This Is For"])
          ]),
          el("div", { className: "about-section-body" }, [
            el("div", { className: "about-audience-grid" },
              AUDIENCES.map(createAudienceCard)
            ),
            el("p", { className: "about-audience-note" }, [
              "You don't need to be bearish. You don't need to be a professional. You just need to care about seeing clearly."
            ])
          ])
        ]),

        // Final Word
        el("section", { className: "about-section reveal-section about-final-word" }, [
          el("div", { className: "about-section-header" }, [
            el("span", { className: "about-section-icon" }, ["⊗"]),
            el("h2", { className: "about-section-title" }, ["Final Word"])
          ]),
          el("div", { className: "about-section-body" }, [
            el("p", { className: "about-lead-paragraph" }, [
              "Capitulators.io is proof that AI can build serious systems — if you treat it like an engineer, not an oracle."
            ]),
            el("p", { className: "about-emphasis-block" }, [
              "No mysticism. No black boxes. No vibes."
            ]),
            el("p", {}, [
              "Just signals, structure, and discipline."
            ])
          ])
        ]),

        // CTA Section
        el("section", { className: "about-cta reveal-section" }, [
          el("p", { className: "about-cta-lead" }, [
            "Ready to watch the gauges instead of the headlines?"
          ]),
          el("div", { className: "about-cta-buttons" }, [
            el("a", { href: "#/observatories", className: "about-btn about-btn-primary" }, [
              "Enter the Observatory Network"
            ]),
            el("a", { href: "#/signals", className: "about-btn about-btn-secondary" }, [
              "Explore Signals"
            ]),
            el("a", { href: "#/manifesto", className: "about-btn about-btn-ghost" }, [
              "Read the Manifesto"
            ])
          ])
        ])
      ]),

      // Footer
      el("footer", { className: "about-footer" }, [
        el("p", { className: "about-footer-text" }, [
          "Built by the doom-pilled, for the doom-curious."
        ]),
        el("p", { className: "about-footer-disclaimer" }, [
          "This platform does not provide investment advice. We track stress. You make decisions."
        ])
      ])
    ])
  ]);
}

/**
 * Initializes scroll reveal effects after DOM insertion.
 */
export function initAboutEffects() {
  const sections = document.querySelectorAll(".about-page .reveal-section");
  if (!sections.length) return;

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReducedMotion) {
    sections.forEach(section => section.classList.add("is-visible"));
    return;
  }

  // Setup IntersectionObserver for scroll reveals
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      }
    });
  }, {
    root: null,
    rootMargin: "0px 0px -50px 0px",
    threshold: 0.15
  });

  sections.forEach(section => observer.observe(section));
}

export { FEATURES, BUILD_PRINCIPLES, AUDIENCES, AI_PRINCIPLES, PHILOSOPHY_PRINCIPLES, PLATFORM_RESULTS };
